import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CreditCard, Loader2, Info } from "lucide-react";
import { createPortalSessionApi, getMyBillingSummaryApi } from "@/lib/billing-api";
import { formatBillingAmount, type BillingSummary } from "@/lib/billing/billing-summary";
import { formatDateLocale } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import logger from "@/lib/logging";
import { BROWSE_PLANS_PATH } from "@/components/subscriptions/lib/plans-path";

const STATUS_BADGE_VARIANTS: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
    active: "default",
    trialing: "outline",
    canceled: "destructive",
    expired: "destructive",
    past_due: "destructive",
    incomplete: "secondary",
};

function StatusBadge({ status }: { status: string }) {
    return (
        <Badge variant={STATUS_BADGE_VARIANTS[status] || "secondary"} data-testid="subscription-billing-status-badge">
            {status}
        </Badge>
    );
}

function ChoosePlanCta({ variant }: { variant: "default" | "outline" }) {
    return (
        <Button asChild variant={variant} size="sm" data-testid="subscription-billing-choose-plan">
            <Link to={BROWSE_PLANS_PATH}>Choose a plan</Link>
        </Button>
    );
}

function ManageSubscriptionButton({ onManage, loading }: { onManage: () => void; loading: boolean }) {
    return (
        <Button
            size="sm"
            onClick={onManage}
            disabled={loading}
            data-testid="subscription-billing-manage-button"
        >
            {loading ? (
                <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Redirecting…
                </>
            ) : (
                "Manage subscription"
            )}
        </Button>
    );
}

function SubscriptionBillingCardBody({
    summary,
    onManageSubscription,
    manageLoading,
}: {
    summary: BillingSummary;
    onManageSubscription: () => void;
    manageLoading: boolean;
}) {
    const price = formatBillingAmount(summary.amount, summary.currency, summary.interval);

    if (summary.state === "active") {
        return (
            <div className="space-y-3" data-testid="subscription-billing-state-active">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm">{summary.planName ?? "Subscription"}</span>
                    {summary.status && <StatusBadge status={summary.status} />}
                </div>
                {price && <p className="text-xs text-muted-foreground">{price}</p>}
                <p className="text-xs text-muted-foreground">
                    {summary.isLifetime
                        ? "Lifetime access — no renewal."
                        : summary.currentPeriodEnd
                            ? summary.cancelAtPeriodEnd
                                ? `Cancels on ${formatDateLocale(summary.currentPeriodEnd)}.`
                                : `Renews on ${formatDateLocale(summary.currentPeriodEnd)}.`
                            : "Renewal date unavailable."}
                </p>
                {summary.accessFromRole && (
                    <Alert data-testid="billing-access-from-role-note">
                        <Info className="h-4 w-4" />
                        <AlertTitle>You don't need this subscription</AlertTitle>
                        <AlertDescription className="text-xs">
                            Your administrator role grants access to the whole application on its
                            own — a licence is not required for it. This subscription is still
                            active and still being billed, so it is shown here and you can cancel
                            it below. Cancelling will not affect your access.
                        </AlertDescription>
                    </Alert>
                )}
                {summary.canManageInStripe && (
                    <ManageSubscriptionButton onManage={onManageSubscription} loading={manageLoading} />
                )}
            </div>
        );
    }

    if (summary.state === "admin-granted") {
        return (
            <div className="space-y-3" data-testid="subscription-billing-state-admin-granted">
                <p className="text-sm font-medium">Granted by your administrator</p>
                <p className="text-xs text-muted-foreground">
                    {summary.currentPeriodEnd
                        ? `Access until ${formatDateLocale(summary.currentPeriodEnd)}.`
                        : "No expiry on record."}
                </p>
                <p className="text-xs text-muted-foreground">
                    You can also set up your own paid plan, independent of this access.
                </p>
                <ChoosePlanCta variant="outline" />
            </div>
        );
    }

    if (summary.state === "role-access") {
        return (
            <div className="space-y-3" data-testid="subscription-billing-state-role-access">
                <p className="text-sm text-muted-foreground">
                    Your access comes from your account, not a purchase — there is nothing to
                    manage here.
                </p>
            </div>
        );
    }

    if (summary.state === "expired") {
        return (
            <div className="space-y-3" data-testid="subscription-billing-state-expired">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm">{summary.planName ?? "Subscription"}</span>
                    {summary.status && <StatusBadge status={summary.status} />}
                </div>
                <p className="text-xs text-muted-foreground">
                    {summary.currentPeriodEnd
                        ? `Ended on ${formatDateLocale(summary.currentPeriodEnd)}.`
                        : "This subscription is no longer active."}
                </p>
                <ChoosePlanCta variant="default" />
            </div>
        );
    }

    return (
        <div className="space-y-3" data-testid="subscription-billing-state-none">
            <p className="text-sm text-muted-foreground">You don&apos;t have an active subscription.</p>
            <ChoosePlanCta variant="default" />
        </div>
    );
}

export default function SubscriptionBillingCard() {
    const [summary, setSummary] = useState<BillingSummary | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [manageLoading, setManageLoading] = useState(false);
    const { toast } = useToast();

    useEffect(() => {
        let cancelled = false;

        (async () => {
            setLoading(true);
            setError(null);
            try {
                const result = await getMyBillingSummaryApi();
                if (!cancelled) setSummary(result);
            } catch (err) {
                logger.error("Error fetching billing summary:", err);
                if (!cancelled) setError("Could not load your subscription details. Please try again later.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, []);

    const handleManageSubscription = async () => {
        setManageLoading(true);
        try {
            const result = await createPortalSessionApi();
            window.location.href = result.url;
        } catch (err) {
            logger.error("Error creating portal session:", err);
            const message = err instanceof Error ? err.message : "Could not open the billing portal.";
            toast({ variant: "destructive", title: "Billing portal unavailable", description: message });
            setManageLoading(false);
        }
    };

    return (
        <Card data-testid="subscription-billing-card">
            <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg text-primary">
                    <CreditCard className="h-4 w-4 text-primary" />
                    Subscription & Billing
                </CardTitle>
                <CardDescription className="text-sm">
                    Your plan, status and renewal information.
                </CardDescription>
            </CardHeader>
            <CardContent className="pt-0">
                {loading && (
                    <p className="text-sm text-muted-foreground" data-testid="subscription-billing-loading">
                        Loading your subscription…
                    </p>
                )}
                {!loading && error && (
                    <p className="text-sm text-destructive" data-testid="subscription-billing-error">
                        {error}
                    </p>
                )}
                {!loading && !error && summary && (
                    <SubscriptionBillingCardBody
                        summary={summary}
                        onManageSubscription={handleManageSubscription}
                        manageLoading={manageLoading}
                    />
                )}
            </CardContent>
        </Card>
    );
}
